import {
  DelegationLogic,
  type IModuleDelegation,
} from '@framework/frontoffice-submission/module/delegation/DelegationLogic.ts';
import { type IDelegation } from '@framework/frontoffice-submission/module/delegation/IDelegation.ts';
import { useMemo } from 'react';
import { type EditModulesDelegationsValues } from '../components/EditModulesDelegationsSchema.ts';

/**
 * Hook per il parsing di tutti i dati relativi alle deleghe sui moduli,
 * utilizzato per la gestione massiva delle deleghe sui moduli
 * (drawer di modifica delle deleghe)
 */
export function useModuleDelegation(modulesDelegations: IModuleDelegation[]) {
  const {
    initialValues,
    compilation,
    hasAtleastOneSignableModule,
    sign,
    view,
    isDifferent,
  } = useMemo(() => {
    // Recupero i dati sulle deleghe di compilazione e calcolo la lista dei delegati unici
    const compilation = modulesDelegations
      .map(moduleDelegation => moduleDelegation.compilation)
      .filter(Boolean) as IDelegation[];

    const areAllCompilationsDelegates =
      DelegationLogic.checkAllModuleHasDelegation(
        compilation,
        modulesDelegations,
      );
    const uniqueCompilation = DelegationLogic.getUniqueDelegations(compilation);

    // Recupero i dati sulle deleghe di firma e calcolo la lista dei delegati unici
    const sign = modulesDelegations
      .map(moduleDelegation => moduleDelegation.sign)
      .filter(Boolean) as IDelegation[];
    const areAllSignsDelegates = DelegationLogic.checkAllModuleHasDelegation(
      sign,
      modulesDelegations,
    );
    const uniqueSign = DelegationLogic.getUniqueDelegations(sign);

    // Recupero i dati sulle deleghe di visualizzazione e calcolo la lista dei delegati unici
    const view = modulesDelegations.map(
      moduleDelegation => moduleDelegation.view,
    );
    const uniqueView = DelegationLogic.getUniqueDelegations(
      view.flatMap(d => d),
    );

    const isViewDelegationUnique = DelegationLogic.checkUniqueViewsDelegations(
      view,
      uniqueView,
    );

    const hasAtleastOneSignableModule =
      DelegationLogic.hasAtleastOneSignableModule(modulesDelegations);

    // Calcolo dei valori iniziali per il form di modifica delle deleghe
    // Se i delegati sono uguali per tutti i moduli li mostro, altrimenti
    // verrà mostrato un messaggio di warning e sarà richiesto all'utente
    // di confermare la modifica
    const initialValues: EditModulesDelegationsValues = {
      compilation: {
        enable: DelegationLogic.getInitialEnableFlag(compilation),
        confirmUpdate: DelegationLogic.getInitialConfirmUpdateFlag(
          uniqueCompilation,
          areAllCompilationsDelegates,
        ),
        delegate:
          uniqueCompilation.length === 1 ? uniqueCompilation[0].fiscalCode : '',
      },
      sign: {
        enable: DelegationLogic.getInitialEnableFlag(sign),
        confirmUpdate: DelegationLogic.getInitialConfirmUpdateFlag(
          uniqueSign,
          areAllSignsDelegates,
        ),
        delegate:
          uniqueSign.length === 1 && areAllSignsDelegates
            ? uniqueSign[0].fiscalCode
            : '',
      },
      view: {
        enable: DelegationLogic.getInitialEnableFlag(uniqueView),
        confirmUpdate: isViewDelegationUnique,
        delegate: isViewDelegationUnique
          ? (uniqueView.map(v => v.fiscalCode).filter(Boolean) as string[])
          : [],
      },
    };

    return {
      initialValues,
      hasAtleastOneSignableModule,
      compilation: uniqueCompilation,
      sign: uniqueSign,
      view: uniqueView,
      /** Flag che indica se i delegati sono differenti tra moduli multipli */
      isDifferent: {
        compilation: !initialValues.compilation.confirmUpdate,
        sign: !initialValues.sign.confirmUpdate,
        view: !initialValues.view.confirmUpdate,
      },
    };
  }, [modulesDelegations]);

  return {
    initialValues,
    hasAtleastOneSignableModule,
    compilation,
    sign,
    view,
    isDifferent,
  };
}
